import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { 
  TrendingUp, 
  TrendingDown,
  Wallet,
  Building2,
  RefreshCcw,
  AlertCircle
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

// Get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem("prosperMindToken") || sessionStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Format currency
const formatCurrency = (amount, currency = "USD") => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// Color palette for assets and debts
const COLORS = {
  // Assets - Blue/Green tones
  savings: '#22c55e',          // Green for savings
  checking: '#16a34a',         // Darker green for checking
  cash: '#15803d',             // Even darker green for cash
  emergencyFund: '#10b981',    // Emerald for emergency fund
  investments: '#3b82f6',      // Blue for investments
  retirement: '#1e40af',       // Dark blue for retirement
  realEstate: '#06b6d4',       // Cyan for real estate
  vehicles: '#0891b2',         // Darker cyan for vehicles
  otherAssets: '#6366f1',      // Indigo for other assets
  
  // Debts - Red/Orange tones
  creditCards: '#ef4444',      // Red for credit cards
  mortgage: '#f97316',         // Orange for mortgage
  carLoans: '#f59e0b',         // Amber for car loans
  studentLoans: '#eab308',     // Yellow for student loans
  personalLoans: '#dc2626',    // Darker red for personal loans
  otherDebt: '#b91c1c',        // Dark red for other debt
  
  // Net Worth
  netWorthPositive: '#581c87', // Purple matching the net worth box (purple-900)
  netWorthNegative: '#991b1b', // Dark red for negative net worth
};

const NetWorthChartModule = ({ onNavigateToMyNumbers }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [myNumbers, setMyNumbers] = useState(null);
  const [hasData, setHasData] = useState(false);

  // Fetch my numbers data
  const fetchMyNumbers = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/api/my-numbers`, { headers: getAuthHeaders() });
      
      if (response.data) {
        setMyNumbers(response.data);
        // Check if user has entered meaningful data
        const assetsObj = response.data.assets || {};
        const debtsObj = response.data.debts || {};
        
        const hasAssets = (response.data.total_assets || 0) > 0 ||
                         (assetsObj.savings || 0) > 0 || 
                         (assetsObj.checking || 0) > 0 ||
                         (assetsObj.investments || 0) > 0 ||
                         (assetsObj.retirement_accounts || 0) > 0 ||
                         (assetsObj.real_estate || 0) > 0 ||
                         (assetsObj.vehicles || 0) > 0;
        const hasDebts = (response.data.total_debt || 0) > 0 ||
                        (debtsObj.credit_cards || 0) > 0 || 
                        (debtsObj.mortgage || 0) > 0 ||
                        (debtsObj.car_loans || 0) > 0 ||
                        (debtsObj.student_loans || 0) > 0;
        setHasData(hasAssets || hasDebts);
      }
    } catch (error) {
      console.error("Error fetching my numbers:", error);
      setHasData(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMyNumbers();
    
    // Listen for updates from MyNumbersModule
    const handleMyNumbersUpdate = () => {
      fetchMyNumbers();
    };
    
    window.addEventListener('myNumbersUpdated', handleMyNumbersUpdate);
    return () => window.removeEventListener('myNumbersUpdated', handleMyNumbersUpdate);
  }, []);

  // Calculate values
  const calculateValues = () => {
    if (!myNumbers) {
      return {
        savings: 0,
        checking: 0,
        cash: 0,
        emergencyFund: 0,
        investments: 0,
        retirement: 0,
        realEstate: 0,
        vehicles: 0,
        otherAssets: 0,
        totalAssets: 0,
        creditCards: 0,
        mortgage: 0,
        carLoans: 0,
        studentLoans: 0,
        personalLoans: 0,
        otherDebt: 0,
        totalDebt: 0,
        netWorth: 0
      };
    }

    // Assets - handle both flat and nested structure
    const assetsObj = myNumbers.assets || {};
    const savings = parseFloat(assetsObj.savings || myNumbers.savings) || 0;
    const checking = parseFloat(assetsObj.checking || myNumbers.checking) || 0;
    const cash = parseFloat(assetsObj.cash || myNumbers.cash) || 0;
    const emergencyFund = parseFloat(assetsObj.emergency_fund || myNumbers.emergency_fund) || 0;
    const investments = parseFloat(assetsObj.investments || myNumbers.investments) || 0;
    const retirement = parseFloat(assetsObj.retirement_accounts || myNumbers.retirement_accounts) || 0;
    const realEstate = parseFloat(assetsObj.real_estate || myNumbers.real_estate) || 0;
    const vehicles = parseFloat(assetsObj.vehicles || myNumbers.vehicles) || 0;
    const otherAssets = parseFloat(assetsObj.other_assets || myNumbers.other_assets) || 0;
    
    // Calculate total or use stored total
    let totalAssets = savings + checking + cash + emergencyFund + investments + retirement + realEstate + vehicles + otherAssets;
    if (totalAssets === 0 && myNumbers.total_assets) {
      totalAssets = parseFloat(myNumbers.total_assets) || 0;
    }

    // Debts - handle both flat and nested structure
    const debtsObj = myNumbers.debts || {};
    const creditCards = parseFloat(debtsObj.credit_cards || myNumbers.credit_cards) || 0;
    const mortgage = parseFloat(debtsObj.mortgage || myNumbers.mortgage) || 0;
    const carLoans = parseFloat(debtsObj.car_loans || myNumbers.car_loans) || 0;
    const studentLoans = parseFloat(debtsObj.student_loans || myNumbers.student_loans) || 0;
    const personalLoans = parseFloat(debtsObj.personal_loans || myNumbers.personal_loans) || 0;
    const otherDebt = parseFloat(debtsObj.other_debt || myNumbers.other_debt) || 0;
    
    // Calculate total or use stored total
    let totalDebt = creditCards + mortgage + carLoans + studentLoans + personalLoans + otherDebt;
    if (totalDebt === 0 && myNumbers.total_debt) {
      totalDebt = parseFloat(myNumbers.total_debt) || 0;
    }

    const netWorth = totalAssets - totalDebt;

    return {
      savings,
      checking,
      cash,
      emergencyFund,
      investments,
      retirement,
      realEstate,
      vehicles,
      otherAssets,
      totalAssets,
      creditCards,
      mortgage,
      carLoans,
      studentLoans,
      personalLoans,
      otherDebt,
      totalDebt,
      netWorth
    };
  };

  const values = calculateValues();
  
  // Check if we have breakdown data or just totals
  const hasAssetBreakdown = values.savings > 0 || values.checking > 0 || values.cash > 0 || 
                            values.emergencyFund > 0 || values.investments > 0 || values.retirement > 0 || 
                            values.realEstate > 0 || values.vehicles > 0 || values.otherAssets > 0;
  const hasDebtBreakdown = values.creditCards > 0 || values.mortgage > 0 || values.carLoans > 0 ||
                          values.studentLoans > 0 || values.personalLoans > 0 || values.otherDebt > 0;

  // Prepare chart data - stacked bars
  const chartData = [
    {
      category: t('netWorthChart.assetsCategory'),
      savings: values.savings,
      checking: values.checking,
      cash: values.cash,
      emergencyFund: values.emergencyFund,
      investments: values.investments,
      retirement: values.retirement,
      realEstate: values.realEstate,
      vehicles: values.vehicles,
      otherAssets: values.otherAssets,
      // If no breakdown, show total as "otherAssets" (will display as single bar)
      ...((!hasAssetBreakdown && values.totalAssets > 0) ? { totalAssetsBar: values.totalAssets } : {}),
      total: values.totalAssets
    },
    {
      category: t('netWorthChart.debtCategory'),
      creditCards: values.creditCards,
      mortgage: values.mortgage,
      carLoans: values.carLoans,
      studentLoans: values.studentLoans,
      personalLoans: values.personalLoans,
      otherDebt: values.otherDebt,
      // If no breakdown, show total as "otherDebt" (will display as single bar)
      ...((!hasDebtBreakdown && values.totalDebt > 0) ? { totalDebtBar: values.totalDebt } : {}),
      total: values.totalDebt
    },
    {
      category: t('netWorthChart.netWorthCategory'),
      netWorth: Math.abs(values.netWorth),
      isNegative: values.netWorth < 0,
      total: values.netWorth
    }
  ];

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-white font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => {
            if (entry.value > 0) {
              const labels = {
                savings: t('netWorthChart.savings'),
                checking: t('netWorthChart.checking'),
                cash: t('netWorthChart.cash'),
                emergencyFund: t('netWorthChart.emergencyFund'),
                investments: t('netWorthChart.investments'),
                retirement: t('netWorthChart.retirement'),
                realEstate: t('netWorthChart.realEstate'),
                vehicles: t('netWorthChart.vehicles'),
                otherAssets: t('netWorthChart.otherAssets'),
                totalAssetsBar: t('netWorthChart.totalAssets'),
                creditCards: t('netWorthChart.creditCards'),
                mortgage: t('netWorthChart.mortgage'),
                carLoans: t('netWorthChart.carLoans'),
                studentLoans: t('netWorthChart.studentLoans'),
                personalLoans: t('netWorthChart.personalLoans'),
                otherDebt: t('netWorthChart.otherDebt'),
                totalDebtBar: t('netWorthChart.totalDebt'),
                netWorth: payload[0]?.payload?.isNegative ? t('netWorthChart.negativeNetWorth') : t('netWorthChart.netWorth')
              };
              
              return (
                <p key={index} style={{ color: entry.fill }} className="text-sm">
                  {labels[entry.name] || entry.name}: {formatCurrency(entry.value)}
                </p>
              );
            }
            return null;
          })}
          {label !== t('netWorthChart.netWorthCategory') && (
            <p className="text-gray-400 text-xs mt-1 border-t border-gray-700 pt-1">
              {t('netWorthChart.total')}: {formatCurrency(payload[0]?.payload?.total)}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Loading state
  if (isLoading) {
    return (
      <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white" data-testid="networth-chart-loading">
        <CardContent className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!hasData) {
    return (
      <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white" data-testid="networth-chart-no-data">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl font-bold text-white">
            <Building2 className="w-6 h-6 text-emerald-500" />
            {t('netWorthChart.noDataTitle')}
          </CardTitle>
          <CardDescription className="text-gray-400">
            {t('netWorthChart.noDataDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">
              {t('netWorthChart.noDataMessage')}
            </p>
            {onNavigateToMyNumbers && (
              <Button
                onClick={onNavigateToMyNumbers}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Wallet className="w-4 h-4 mr-2" />
                {t('netWorthChart.enterData')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white" data-testid="networth-chart-module">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl font-bold text-white">
              <Building2 className="w-6 h-6 text-emerald-500" />
              {t('netWorthChart.title')}
            </CardTitle>
            <CardDescription className="text-gray-400">
              {t('netWorthChart.description')}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchMyNumbers}
            className="text-gray-400 hover:text-white"
          >
            <RefreshCcw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-700/50">
            <p className="text-blue-400 text-xs uppercase tracking-wide mb-1">{t('netWorthChart.totalAssets')}</p>
            <p className="text-white text-xl font-bold">{formatCurrency(values.totalAssets)}</p>
            <div className="mt-2 space-y-1 text-xs">
              {values.savings > 0 && (
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{backgroundColor: COLORS.savings}}></span>
                  <span className="text-gray-400">{t('netWorthChart.savings')}: {formatCurrency(values.savings)}</span>
                </div>
              )}
              {values.investments > 0 && (
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{backgroundColor: COLORS.investments}}></span>
                  <span className="text-gray-400">{t('netWorthChart.investments')}: {formatCurrency(values.investments)}</span>
                </div>
              )}
              {values.realEstate > 0 && (
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{backgroundColor: COLORS.realEstate}}></span>
                  <span className="text-gray-400">{t('netWorthChart.realEstate')}: {formatCurrency(values.realEstate)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-red-900/30 rounded-lg p-4 border border-red-700/50">
            <p className="text-red-400 text-xs uppercase tracking-wide mb-1">{t('netWorthChart.totalDebt')}</p>
            <p className="text-white text-xl font-bold">{formatCurrency(values.totalDebt)}</p>
            <div className="mt-2 space-y-1 text-xs">
              {values.mortgage > 0 && (
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{backgroundColor: COLORS.mortgage}}></span>
                  <span className="text-gray-400">{t('netWorthChart.mortgage')}: {formatCurrency(values.mortgage)}</span>
                </div>
              )}
              {values.creditCards > 0 && (
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{backgroundColor: COLORS.creditCards}}></span>
                  <span className="text-gray-400">{t('netWorthChart.creditCards')}: {formatCurrency(values.creditCards)}</span>
                </div>
              )}
              {values.carLoans > 0 && (
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{backgroundColor: COLORS.carLoans}}></span>
                  <span className="text-gray-400">{t('netWorthChart.carLoans')}: {formatCurrency(values.carLoans)}</span>
                </div>
              )}
            </div>
          </div>

          <div className={`rounded-lg p-4 border ${
            values.netWorth >= 0 
              ? 'bg-purple-900/30 border-purple-700/50' 
              : 'bg-red-900/40 border-red-600/50'
          }`}>
            <p className={`text-xs uppercase tracking-wide mb-1 ${
              values.netWorth >= 0 ? 'text-purple-400' : 'text-red-400'
            }`}>
              {t('netWorthChart.netWorth')}
            </p>
            <p className="text-white text-xl font-bold">
              {values.netWorth >= 0 ? '+' : ''}{formatCurrency(values.netWorth)}
            </p>
            <div className="mt-2">
              {values.netWorth >= 0 ? (
                <TrendingUp className="w-5 h-5 text-purple-500" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-500" />
              )}
            </div>
          </div>
        </div>

        {/* Stacked Bar Chart */}
        <div className="bg-gray-800/50 rounded-xl p-4 flex justify-center">
          <ResponsiveContainer width="70%" height={300}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
              barCategoryGap="20%"
              barSize={80}
            >
              <XAxis 
                dataKey="category" 
                tick={{ fill: '#9ca3af' }}
                axisLine={{ stroke: '#374151' }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fill: '#9ca3af' }}
                axisLine={{ stroke: '#374151' }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} cursor={false} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                content={({ payload }) => {
                  // Define the desired order
                  const order = [
                    'savings',
                    'checking',
                    'cash',
                    'emergencyFund',
                    'investments',
                    'retirement',
                    'realEstate',
                    'vehicles',
                    'otherAssets',
                    'totalAssetsBar',
                    'creditCards',
                    'mortgage',
                    'carLoans',
                    'studentLoans',
                    'personalLoans',
                    'otherDebt',
                    'totalDebtBar',
                    'netWorth'
                  ];
                  
                  const labels = {
                    savings: t('netWorthChart.savings'),
                    checking: t('netWorthChart.checking'),
                    cash: t('netWorthChart.cash'),
                    emergencyFund: t('netWorthChart.emergencyFund'),
                    investments: t('netWorthChart.investments'),
                    retirement: t('netWorthChart.retirement'),
                    realEstate: t('netWorthChart.realEstate'),
                    vehicles: t('netWorthChart.vehicles'),
                    otherAssets: t('netWorthChart.otherAssets'),
                    totalAssetsBar: t('netWorthChart.totalAssets'),
                    creditCards: t('netWorthChart.creditCards'),
                    mortgage: t('netWorthChart.mortgage'),
                    carLoans: t('netWorthChart.carLoans'),
                    studentLoans: t('netWorthChart.studentLoans'),
                    personalLoans: t('netWorthChart.personalLoans'),
                    otherDebt: t('netWorthChart.otherDebt'),
                    totalDebtBar: t('netWorthChart.totalDebt'),
                    netWorth: t('netWorthChart.netWorth')
                  };
                  
                  // Sort payload by desired order and filter out zero values
                  const sortedPayload = [...payload]
                    .filter(p => {
                      const dataPoint = chartData.find(d => d[p.dataKey] > 0);
                      return dataPoint !== undefined;
                    })
                    .sort((a, b) => {
                      return order.indexOf(a.dataKey) - order.indexOf(b.dataKey);
                    });
                  
                  return (
                    <div className="flex flex-wrap justify-center gap-3 pt-2">
                      {sortedPayload.map((entry, index) => (
                        <div key={`legend-${index}`} className="flex items-center gap-1.5">
                          <div 
                            className="w-3 h-3 rounded-sm" 
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="text-gray-300 text-xs">
                            {labels[entry.dataKey] || entry.dataKey}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              <ReferenceLine y={0} stroke="#6b7280" />
              
              {/* Asset bars (green/blue tones) */}
              <Bar dataKey="savings" stackId="a" fill={COLORS.savings} name="savings" />
              <Bar dataKey="checking" stackId="a" fill={COLORS.checking} name="checking" />
              <Bar dataKey="cash" stackId="a" fill={COLORS.cash} name="cash" />
              <Bar dataKey="emergencyFund" stackId="a" fill={COLORS.emergencyFund} name="emergencyFund" />
              <Bar dataKey="investments" stackId="a" fill={COLORS.investments} name="investments" />
              <Bar dataKey="retirement" stackId="a" fill={COLORS.retirement} name="retirement" />
              <Bar dataKey="realEstate" stackId="a" fill={COLORS.realEstate} name="realEstate" />
              <Bar dataKey="vehicles" stackId="a" fill={COLORS.vehicles} name="vehicles" />
              <Bar dataKey="otherAssets" stackId="a" fill={COLORS.otherAssets} radius={[4, 4, 0, 0]} name="otherAssets" />
              {/* Fallback bar when only total is available */}
              <Bar dataKey="totalAssetsBar" stackId="a" fill={COLORS.savings} radius={[4, 4, 0, 0]} name="totalAssetsBar" />
              
              {/* Debt bars (red/orange tones) */}
              <Bar dataKey="creditCards" stackId="a" fill={COLORS.creditCards} name="creditCards" />
              <Bar dataKey="mortgage" stackId="a" fill={COLORS.mortgage} name="mortgage" />
              <Bar dataKey="carLoans" stackId="a" fill={COLORS.carLoans} name="carLoans" />
              <Bar dataKey="studentLoans" stackId="a" fill={COLORS.studentLoans} name="studentLoans" />
              <Bar dataKey="personalLoans" stackId="a" fill={COLORS.personalLoans} name="personalLoans" />
              <Bar dataKey="otherDebt" stackId="a" fill={COLORS.otherDebt} radius={[4, 4, 0, 0]} name="otherDebt" />
              {/* Fallback bar when only total is available */}
              <Bar dataKey="totalDebtBar" stackId="a" fill={COLORS.creditCards} radius={[4, 4, 0, 0]} name="totalDebtBar" />
              
              {/* Net Worth bar */}
              <Bar 
                dataKey="netWorth" 
                stackId="a"
                fill={COLORS.netWorthPositive}
                radius={[4, 4, 0, 0]}
                name="netWorth"
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.isNegative ? COLORS.netWorthNegative : COLORS.netWorthPositive} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Insights */}
        <div className="bg-gray-800/30 rounded-lg p-4 border border-gray-700">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-purple-500" />
            Quick Insights
          </h4>
          <div className="space-y-3 text-sm text-gray-400">
            {/* 1. Net Worth Status */}
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-purple-500 mt-1.5 flex-shrink-0"></span>
              <p>
                {values.netWorth >= 0 ? (
                  <>
                    Your net worth is <span className="text-purple-400 font-medium">{formatCurrency(values.netWorth)}</span>, 
                    meaning your assets exceed your debts by <span className="text-purple-400 font-medium">
                      {values.totalDebt > 0 ? ((values.netWorth / values.totalDebt) * 100).toFixed(0) : '∞'}%
                    </span>.
                  </>
                ) : (
                  <>
                    Your net worth is <span className="text-red-400 font-medium">{formatCurrency(values.netWorth)}</span>. 
                    Your debts exceed your assets by <span className="text-red-400 font-medium">{formatCurrency(Math.abs(values.netWorth))}</span>.
                  </>
                )}
              </p>
            </div>
            
            {/* 2. Debt to Asset Ratio */}
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 flex-shrink-0"></span>
              <p>
                Your debt-to-asset ratio is <span className="text-orange-400 font-medium">
                  {values.totalAssets > 0 ? ((values.totalDebt / values.totalAssets) * 100).toFixed(1) : 0}%
                </span>
                {values.totalAssets > 0 && values.totalDebt / values.totalAssets < 0.5 && (
                  <span className="text-green-400"> (healthy range)</span>
                )}
                {values.totalAssets > 0 && values.totalDebt / values.totalAssets >= 0.5 && values.totalDebt / values.totalAssets < 0.8 && (
                  <span className="text-yellow-400"> (moderate)</span>
                )}
                {values.totalAssets > 0 && values.totalDebt / values.totalAssets >= 0.8 && (
                  <span className="text-red-400"> (high leverage)</span>
                )}
              </p>
            </div>
            
            {/* 3. Liquid vs Illiquid Assets */}
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></span>
              <p>
                {(() => {
                  const liquidAssets = values.savings + values.checking + values.cash + values.emergencyFund + values.investments;
                  const illiquidAssets = values.realEstate + values.vehicles + values.retirement;
                  const liquidPct = values.totalAssets > 0 ? (liquidAssets / values.totalAssets) * 100 : 0;
                  return (
                    <>
                      <span className="text-blue-400 font-medium">{liquidPct.toFixed(0)}%</span> of your assets 
                      (<span className="text-blue-400 font-medium">{formatCurrency(liquidAssets)}</span>) are liquid 
                      (easily accessible), while <span className="text-cyan-400 font-medium">{formatCurrency(illiquidAssets)}</span> are 
                      in illiquid assets like real estate and retirement accounts.
                    </>
                  );
                })()}
              </p>
            </div>
          </div>
        </div>

        {/* Edit Data Button */}
        {onNavigateToMyNumbers && (
          <div className="text-center pt-2">
            <Button
              variant="outline"
              onClick={onNavigateToMyNumbers}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <Wallet className="w-4 h-4 mr-2" />
              Update My Numbers
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NetWorthChartModule;
